(function attachCustomizerCore(root) {
  'use strict';

  const FRACTION_LABELS = new Map([
    [0, ''],
    [0.125, '1/8'],
    [0.25, '1/4'],
    [0.375, '3/8'],
    [0.5, '1/2'],
    [0.625, '5/8'],
    [0.75, '3/4'],
    [0.875, '7/8']
  ]);

  function normalizeDimension(whole, fraction) {
    const wholeNumber = Number(whole);
    const fractionNumber = Number(fraction || 0);

    if (!Number.isFinite(wholeNumber) || !Number.isFinite(fractionNumber)) {
      return Number.NaN;
    }

    return wholeNumber + fractionNumber;
  }

  function validateSelection(selection, config) {
    const errors = {};
    const width = normalizeDimension(selection.widthWhole, selection.widthFraction);
    const height = normalizeDimension(selection.heightWhole, selection.heightFraction);

    if (config.installationFamily === 'no-drill' && selection.mountType !== 'inside') {
      errors.mountType = 'No-drill shades require an inside mount.';
    }

    if (!Number.isFinite(width) || width < config.minWidth || width > config.maxWidth) {
      errors.width = `Width must be between ${config.minWidth} and ${config.maxWidth} inches.`;
    }

    if (!Number.isFinite(height) || height < config.minHeight || height > config.maxHeight) {
      errors.height = `Height must be between ${config.minHeight} and ${config.maxHeight} inches.`;
    }

    const allowedFractions = config.allowedFractions || [0];
    if (!allowedFractions.includes(Number(selection.widthFraction || 0))) {
      errors.widthFraction = 'Select an available width fraction.';
    }
    if (!allowedFractions.includes(Number(selection.heightFraction || 0))) {
      errors.heightFraction = 'Select an available height fraction.';
    }

    return errors;
  }

  function calculatePriceCents(selection, config) {
    const width = normalizeDimension(selection.widthWhole, selection.widthFraction);
    const height = normalizeDimension(selection.heightWhole, selection.heightFraction);
    const priceBand = (config.priceBands || []).find(
      (band) => width <= band.maxWidth && height <= band.maxHeight
    );

    if (!priceBand) {
      throw new RangeError('No price is configured for the selected dimensions.');
    }

    const upgradeTotal = (selection.upgrades || []).reduce((total, upgradeCode) => {
      return total + Number((config.upgrades || {})[upgradeCode] || 0);
    }, 0);

    return Number(priceBand.priceCents) + upgradeTotal;
  }

  function formatDimension(whole, fraction) {
    const wholeNumber = Number(whole);
    const fractionNumber = Number(fraction || 0);
    const fractionLabel = FRACTION_LABELS.get(fractionNumber);

    if (!Number.isFinite(wholeNumber) || fractionLabel === undefined) {
      throw new TypeError('Dimension is not valid.');
    }

    return `${wholeNumber}${fractionLabel ? ` ${fractionLabel}` : ''} in`;
  }

  function titleCase(value) {
    return String(value || '')
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  function buildLineItemProperties(selection) {
    const isNoDrill = String(selection.productCode || '').startsWith('NDR-');
    const mountLabel = selection.mountType === 'inside' ? 'Inside Mount' : 'Outside Mount';
    const properties = {
      '_Product Code': selection.productCode,
      'Fabric Code': selection.fabricCode,
      'Color': selection.colorName,
      'Mount Type': `${mountLabel} - ${isNoDrill ? 'No Drill' : 'Drilled'}`,
      'Width': formatDimension(selection.widthWhole, selection.widthFraction),
      'Height': formatDimension(selection.heightWhole, selection.heightFraction),
      'Light Control': titleCase(selection.lightControl)
    };

    if (String(selection.roomName || '').trim()) {
      properties['Room Name'] = String(selection.roomName).trim();
    }
    if (String(selection.customerNote || '').trim()) {
      properties['Customer Note'] = String(selection.customerNote).trim();
    }

    return properties;
  }

  const FRACTION_VALUES = Object.freeze({
    '0': 0,
    '1/8': 0.125,
    '1/4': 0.25,
    '3/8': 0.375,
    '1/2': 0.5,
    '5/8': 0.625,
    '3/4': 0.75,
    '7/8': 0.875
  });

  const ROMAN_COMPATIBILITY_RULE_PACK = Object.freeze({
    id: 'no-drill-roman-v1',
    version: 1,
    productCode: 'NDR-ROMAN',
    fixed: Object.freeze({
      mechanism: 'cordless-roman',
      mountType: 'inside',
      installationFamily: 'no-drill',
      minimumDepth: 1.6
    }),
    dimensions: Object.freeze({
      width: Object.freeze({ min: 20, max: 79 }),
      height: Object.freeze({ min: 22, max: 78 })
    }),
    allowedFractions: Object.freeze(Object.keys(FRACTION_VALUES)),
    pricing: Object.freeze({
      type: 'increment-grid',
      source: 'RomanPricingData',
      baseKey: 'ROMAN_BASE_PRICE',
      widthKey: 'ROMAN_WIDTH_INCREMENTS',
      heightKey: 'ROMAN_HEIGHT_INCREMENTS',
      fractionsAffectPrice: false,
      currency: 'USD'
    }),
    fabrics: Object.freeze([
      { code: 'LM3108-White', name: 'Cloud White', lightControl: 'LF' },
      { code: 'LM3108-1', name: 'Natural Flax', lightControl: 'LF' },
      { code: 'LM3109-1', name: 'Silver Mist', lightControl: 'LF' },
      { code: 'LM3109-3', name: 'Stone Gray', lightControl: 'LF' },
      { code: 'LM3109-4', name: 'Deep Gray', lightControl: 'LF' },
      { code: 'DE360109-11-0602', name: 'Pearl', lightControl: 'BO' },
      { code: 'DE360109-13-1012', name: 'Camel Taupe', lightControl: 'BO' },
      { code: 'DE360109-16-4705', name: 'Charcoal Gray', lightControl: 'BO' },
      { code: 'DE360109-18', name: 'Walnut Brown', lightControl: 'BO' },
      { code: 'DE360109-19', name: 'Graphite', lightControl: 'BO' },
      { code: 'DE360109-8', name: 'Mushroom', lightControl: 'BO' },
      { code: 'LM3107-1', name: 'Porcelain', lightControl: 'BO' },
      { code: 'YH-220650', name: 'Oatmeal', lightControl: 'BO' },
      { code: 'YH-2332C', name: 'Ink Gray', lightControl: 'BO' },
      { code: 'YH-D241-13-1012', name: 'Mocha', lightControl: 'BO' }
    ])
  });

  function fractionToDecimal(value) {
    const key = String(value ?? '0');
    return Object.prototype.hasOwnProperty.call(FRACTION_VALUES, key)
      ? FRACTION_VALUES[key]
      : Number.NaN;
  }

  function findFabric(rulePack, code) {
    return (rulePack.fabrics || []).find((fabric) => fabric.code === code) || null;
  }

  function fieldError(field, message) {
    return { field, message };
  }

  function validateConfiguration(configuration = {}, rulePack = {}) {
    const errors = [];
    const fabric = findFabric(rulePack, configuration.fabricCode);
    const widthFraction = String(configuration.widthFraction ?? '0');
    const heightFraction = String(configuration.heightFraction ?? '0');
    const widthWhole = Number(configuration.widthWhole);
    const heightWhole = Number(configuration.heightWhole);

    if (!fabric) errors.push(fieldError('fabricCode', 'Please select an available fabric.'));
    if (configuration.mountType !== rulePack.fixed?.mountType) {
      errors.push(fieldError('mountType', 'This product is available for inside mount only.'));
    }
    if (!Number.isInteger(widthWhole) ||
        widthWhole < rulePack.dimensions?.width?.min ||
        widthWhole > rulePack.dimensions?.width?.max) {
      errors.push(fieldError(
        'widthWhole',
        `Width must be between ${rulePack.dimensions.width.min} and ${rulePack.dimensions.width.max} inches.`
      ));
    }
    if (!(rulePack.allowedFractions || []).includes(widthFraction)) {
      errors.push(fieldError('widthFraction', 'Please select an available width fraction.'));
    }
    if (!Number.isInteger(heightWhole) ||
        heightWhole < rulePack.dimensions?.height?.min ||
        heightWhole > rulePack.dimensions?.height?.max) {
      errors.push(fieldError(
        'heightWhole',
        `Height must be between ${rulePack.dimensions.height.min} and ${rulePack.dimensions.height.max} inches.`
      ));
    }
    if (!(rulePack.allowedFractions || []).includes(heightFraction)) {
      errors.push(fieldError('heightFraction', 'Please select an available height fraction.'));
    }
    if (rulePack.fixed?.minimumDepth && configuration.depthConfirmed !== true) {
      errors.push(fieldError(
        'depthConfirmed',
        `Please confirm at least ${rulePack.fixed.minimumDepth} inches of clear mounting depth.`
      ));
    }
    if (!Number.isInteger(configuration.quantity) || configuration.quantity < 1) {
      errors.push(fieldError('quantity', 'Please enter a positive whole-number quantity.'));
    }

    return { valid: errors.length === 0, errors };
  }

  const STEP_FIELDS = Object.freeze({
    fabric: ['fabricCode'],
    fit: ['mountType', 'depthConfirmed'],
    size: ['widthWhole', 'widthFraction', 'heightWhole', 'heightFraction'],
    details: ['quantity'],
    review: []
  });

  function validateStep(stepId, configuration, rulePack) {
    const owned = STEP_FIELDS[stepId] || [];
    const errors = validateConfiguration(configuration, rulePack).errors
      .filter(({ field }) => owned.includes(field));
    return { valid: errors.length === 0, errors };
  }

  function calculatePreviewPrice(configuration, rulePack, pricingData) {
    if (rulePack.pricing?.type !== 'increment-grid') {
      throw new TypeError('The active rule pack does not define supported preview pricing.');
    }
    const width = Number(configuration.widthWhole);
    const height = Number(configuration.heightWhole);
    const widthMap = pricingData[rulePack.pricing.widthKey];
    const heightMap = pricingData[rulePack.pricing.heightKey];
    const base = pricingData[rulePack.pricing.baseKey];
    if (!Object.prototype.hasOwnProperty.call(widthMap, width)) {
      throw new RangeError('Please select a valid priced width.');
    }
    if (!Object.prototype.hasOwnProperty.call(heightMap, height)) {
      throw new RangeError('Please select a valid priced height.');
    }
    return Math.round((base + widthMap[width] + heightMap[height]) * 100) / 100;
  }

  function reconcileConfiguration(configuration, rulePack) {
    const next = { ...configuration };
    const cleared = [];
    if (!findFabric(rulePack, next.fabricCode)) {
      if (next.fabricCode) {
        next.fabricCode = '';
        cleared.push('fabricCode');
      }
      if (next.colorName) {
        next.colorName = '';
        cleared.push('colorName');
      }
    }
    next.mountType = rulePack.fixed?.mountType || next.mountType;
    return { configuration: next, cleared };
  }

  function labelFor(value) {
    return String(value || '')
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  function buildReview(configuration, rulePack) {
    const fabric = findFabric(rulePack, configuration.fabricCode);
    return {
      productCode: rulePack.productCode,
      mechanism: labelFor(rulePack.fixed.mechanism),
      fabric: fabric?.name || '',
      lightControl: fabric?.lightControl === 'LF'
        ? 'Light Filtering'
        : fabric?.lightControl === 'BO'
          ? 'Blackout'
          : labelFor(fabric?.lightControl),
      mount: `${labelFor(rulePack.fixed.mountType)} Mount - ${labelFor(rulePack.fixed.installationFamily)}`,
      depth: rulePack.fixed.minimumDepth
        ? `${rulePack.fixed.minimumDepth} in ${
            configuration.depthConfirmed === true ? 'confirmed' : 'not confirmed'
          }`
        : '',
      width: formatDimension(configuration.widthWhole, fractionToDecimal(configuration.widthFraction)),
      height: formatDimension(configuration.heightWhole, fractionToDecimal(configuration.heightFraction)),
      quantity: configuration.quantity,
      roomName: String(configuration.roomName || '').trim(),
      customerNote: String(configuration.customerNote || '').trim()
    };
  }

  function buildConfigurationResult(configuration, rulePack, previewPrice) {
    const fabric = findFabric(rulePack, configuration.fabricCode);
    const validation = validateConfiguration(configuration, rulePack);
    return {
      rulePack: { id: rulePack.id, version: rulePack.version },
      productCode: rulePack.productCode,
      mechanism: rulePack.fixed.mechanism,
      fabric: {
        code: fabric?.code || '',
        name: fabric?.name || '',
        lightControl: fabric?.lightControl || ''
      },
      mount: {
        type: rulePack.fixed.mountType,
        installationFamily: rulePack.fixed.installationFamily,
        minimumDepth: rulePack.fixed.minimumDepth,
        confirmed: configuration.depthConfirmed === true
      },
      dimensions: {
        width: {
          whole: Number(configuration.widthWhole),
          fraction: String(configuration.widthFraction ?? '0'),
          normalized: normalizeDimension(
            configuration.widthWhole,
            fractionToDecimal(configuration.widthFraction)
          )
        },
        height: {
          whole: Number(configuration.heightWhole),
          fraction: String(configuration.heightFraction ?? '0'),
          normalized: normalizeDimension(
            configuration.heightWhole,
            fractionToDecimal(configuration.heightFraction)
          )
        }
      },
      options: JSON.parse(JSON.stringify(configuration.options || {})),
      quantity: configuration.quantity,
      notes: {
        roomName: String(configuration.roomName || '').trim(),
        customerNote: String(configuration.customerNote || '').trim()
      },
      previewPrice: {
        currency: rulePack.pricing.currency,
        amount: previewPrice,
        status: Number.isFinite(previewPrice) ? 'ready' : 'unavailable'
      },
      validation: { valid: validation.valid }
    };
  }

  function romanPricingData(pricingData) {
    if (pricingData) return pricingData;
    if (root && root.RomanPricingData) return root.RomanPricingData;
    if (typeof require === 'function') return require('./roman-pricing-data.js');
    throw new Error('Roman pricing data is not loaded.');
  }

  function calculateRomanPreviewPrice(
    configuration = {},
    rulePack = ROMAN_COMPATIBILITY_RULE_PACK,
    pricingData
  ) {
    return calculatePreviewPrice(configuration, rulePack, romanPricingData(pricingData));
  }

  function validateRomanConfiguration(
    configuration = {},
    rulePack = ROMAN_COMPATIBILITY_RULE_PACK
  ) {
    return validateConfiguration({
      ...configuration,
      mountType: configuration.mountType ?? rulePack.fixed.mountType,
      quantity: configuration.quantity ?? 1
    }, rulePack);
  }

  function reconcileRomanFabric(
    configuration = {},
    rulePack = ROMAN_COMPATIBILITY_RULE_PACK
  ) {
    return reconcileConfiguration(configuration, rulePack);
  }

  const api = {
    normalizeDimension,
    formatDimension,
    validateSelection,
    calculatePriceCents,
    buildLineItemProperties,
    fractionToDecimal,
    findFabric,
    validateConfiguration,
    validateStep,
    calculatePreviewPrice,
    reconcileConfiguration,
    buildReview,
    buildConfigurationResult,
    calculateRomanPreviewPrice,
    validateRomanConfiguration,
    reconcileRomanFabric
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (root) {
    root.ShadeCustomizerCore = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
